import { logInfo } from '../utils/logging';
import { REGISTERED_APPS } from '../types/Apps';
import { IntelligemsUserData } from '../types/CustomWindow';
import { ProductVariant } from '../types';
import { INTELLIGEMS_ENABLED_OVERRIDE_KEY } from '../constants/app';
import customLocalStorage from './storage';

export const hasIntelligems = (data: unknown): data is object => {
  const result = typeof data === 'object' && data !== null;
  logInfo(
    `hasIntelligems check - dataType: ${typeof data}, isNull: ${
      data === null
    }, result: ${result}`,
  );
  return result;
};

export const hasIntelligemsUser = (
  data: unknown,
): data is IntelligemsUserData => {
  logInfo(
    `hasIntelligemsUser check - dataType: ${typeof data}, isNull: ${
      data === null
    }`,
  );

  if (!hasIntelligems(data)) {
    logInfo('hasIntelligemsUser: data is not an intelligems object');
    return false;
  }

  const maybeUser = (data as any).user;
  const hasUserObject = typeof maybeUser === 'object' && maybeUser !== null;
  const hasIgId = typeof maybeUser?.igId === 'string';
  const result = hasUserObject && hasIgId;

  logInfo(
    `hasIntelligemsUser: user check - hasUserObject: ${hasUserObject}, hasIgId: ${hasIgId}, igIdType: ${typeof maybeUser?.igId}, igIdValue: ${
      maybeUser?.igId
    }, result: ${result}`,
  );

  return result;
};

export const getIntelligemsUserId = (
  registeredApps: REGISTERED_APPS,
): string | null => {
  logInfo(`getIntelligemsUserId called - registeredApps: ${registeredApps}`);

  if (hasIntelligemsUser(window.igData)) {
    const userId = window.igData!.user!.igId;
    logInfo(
      `Intelligems user ID found - registeredApps: ${registeredApps}, userId: ${userId}`,
    );
    return userId;
  }

  logInfo(
    `No Intelligems user ID available - registeredApps: ${registeredApps}`,
  );
  return null;
};

/**
 * Check if intelligems is actually available and functional
 */
export const isIntelligemsAvailable = (): boolean => {
  const result = !!window.igData;
  logInfo(
    `isIntelligemsAvailable check - hasIgData: ${!!window.igData}, igDataType: ${typeof window.igData}, result: ${result}`,
  );
  return result;
};

/**
 * Check if intelligems is available
 * Returns true if intelligems is actually available, false otherwise
 */
export const ensureIntelligemsAvailability = (): boolean => {
  logInfo('ensureIntelligemsAvailability called');

  const isAvailable = isIntelligemsAvailable();

  if (isAvailable) {
    logInfo('ensureIntelligemsAvailability: intelligems is available');
  } else {
    logInfo('ensureIntelligemsAvailability: intelligems not available');
  }

  return isAvailable;
};

/**
 * Get the Intelligems enabled status with localStorage override support
 * @param originalEnabled - The original enabled status from the app config
 * @returns The final enabled status (original or overridden)
 */
export const getIntelligemsEnabledStatus = (
  originalEnabled: boolean,
): boolean => {
  logInfo(
    `getIntelligemsEnabledStatus called: originalEnabled=${originalEnabled}`,
  );

  // Check localStorage override for intelligems enabled
  const getIntelligemsEnabledOverride = (): boolean | null => {
    const override = customLocalStorage.getItem(
      INTELLIGEMS_ENABLED_OVERRIDE_KEY,
    );
    logInfo(
      `getIntelligemsEnabledOverride: localStorage value=${override}, key=${INTELLIGEMS_ENABLED_OVERRIDE_KEY}`,
    );

    if (override === 'true') return true;
    if (override === 'false') return false;
    return null; // No override set
  };

  const intelligemsOverride = getIntelligemsEnabledOverride();
  const finalStatus =
    intelligemsOverride !== null ? intelligemsOverride : originalEnabled;

  logInfo(
    `getIntelligemsEnabledStatus result: originalEnabled=${originalEnabled}, override=${intelligemsOverride}, finalStatus=${finalStatus}`,
  );

  return finalStatus;
};

/**
 * Parse raw price value from intelligems into a number or undefined
 *
 * This function handles the initial parsing of price data before rounding.
 * It converts various input types to a number that can then be processed
 * by convertIntelligemsPriceToMinorUnits() for proper rounding.
 *
 * Handles various input types:
 * - null, undefined, empty string → returns undefined
 * - number → returns as-is
 * - string (e.g., "29.95") → converts to number using Number()
 * - other types → attempts conversion, returns undefined if NaN
 *
 * @param rawPrice - The raw price value from intelligems (could be string, number, null, etc.)
 * @param context - Context string for logging purposes
 * @returns The parsed number or undefined if parsing fails
 */
export function parseIntelligemsPrice(
  rawPrice: unknown,
  context: string,
): number | undefined {
  if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
    logInfo(`${context}: rawPrice is falsy, returning undefined`);
    return undefined;
  }

  const price = Number(rawPrice);
  if (isNaN(price)) {
    logInfo(
      `${context}: rawPrice cannot be converted to number: ${rawPrice} (type: ${typeof rawPrice})`,
    );
    return undefined;
  }

  logInfo(`${context}: converted to number: ${rawPrice} -> ${price}`);
  return price;
}

/**
 * Convert Intelligems price from major units to minor units with proper rounding
 *
 * Intelligems returns currency strings like "29.95" (major units)
 * Candyrack expects prices in minor units (e.g., 2995 for $29.95)
 *
 * Conversion process:
 * 1. Multiply by 100 to convert major to minor units (29.95 * 100 = 2995.0)
 * 2. Use Math.round() to handle floating point precision issues
 *    - JavaScript floating point arithmetic can cause precision errors
 *    - Example: 29.95 * 100 might result in 2994.9999999999995 instead of 2995
 *    - Math.round() ensures we get the correct integer value
 *
 * Examples:
 * - 29.95 → 2995 (29.95 * 100 = 2995.0 → Math.round(2995.0) = 2995)
 * - 19.99 → 1999 (19.99 * 100 = 1999.0 → Math.round(1999.0) = 1999)
 * - 0.01 → 1 (0.01 * 100 = 1.0 → Math.round(1.0) = 1)
 *
 * @param price - The price in major units (e.g., 29.95)
 * @returns The price in minor units (e.g., 2995) or undefined if price is falsy
 */
export function convertIntelligemsPriceToMinorUnits(
  price: number | undefined,
): number | undefined {
  logInfo(
    `convertIntelligemsPriceToMinorUnits called: price=${price}, priceType=${typeof price}`,
  );

  const finalPrice =
    price !== undefined && price !== null ? Math.round(price * 100) : undefined;

  logInfo(
    `convertIntelligemsPriceToMinorUnits result: input=${price}, output=${finalPrice}, calculation=${
      price !== undefined && price !== null
        ? `${price} * 100 = ${price * 100} → Math.round(${
            price * 100
          }) = ${finalPrice}`
        : 'undefined'
    }`,
  );

  return finalPrice;
}

/**
 * Generic function to get intelligems price by variant ID
 *
 * @param isIntelligemsEnabled - Whether intelligems is enabled
 * @param variantId - The variant ID (can be number or string)
 * @param priceType - The type of price to retrieve ('price' or 'comparePrice')
 * @param context - Optional context for logging (e.g., variant title)
 * @returns The price in minor units or undefined
 */
function getIntelligemsPriceByVariantId(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
  priceType: 'price' | 'comparePrice' = 'price',
  context?: string,
): number | undefined {
  const functionName = `getIntelligems${
    priceType === 'price' ? 'VariantPrice' : 'ComparePriceByVariantId'
  }`;
  const methodName =
    priceType === 'price'
      ? 'getPriceByVariantId'
      : 'getComparePriceByVariantId';

  logInfo(
    `${functionName} called: isIntelligemsEnabled=${isIntelligemsEnabled}, variantId=${variantId}, context=${
      context || 'N/A'
    }`,
  );

  if (!isIntelligemsEnabled || !variantId) {
    logInfo(`${functionName}: early return - not enabled or no variant ID`);
    return undefined;
  }

  // Verify intelligems is actually available at the point of use
  if (!ensureIntelligemsAvailability()) {
    logInfo(
      `${functionName}: intelligems not actually available after verification`,
    );
    return undefined;
  }

  const rawPrice = window.igData?.price?.[methodName]?.(variantId);

  logInfo(
    `Intelligems Variant Debug: variantId=${variantId}, variantIdType=${typeof variantId}, isNumeric=${!isNaN(
      Number(variantId),
    )}, context=${
      context || 'N/A'
    }, ${priceType}=${rawPrice}, rawPriceType=${typeof rawPrice}, hasIgData=${!!window.igData}, hasPriceObject=${!!window
      .igData?.price}, has${methodName}Method=${!!window.igData?.price?.[
      methodName
    ]}`,
  );

  logInfo(
    `${methodName} result: variantId=${variantId}, ${priceType}=${rawPrice}, rawPriceType=${typeof rawPrice}`,
  );

  logInfo(
    `${methodName}: raw result - variantId: ${variantId}, ${priceType}: ${rawPrice}, rawPriceType: ${typeof rawPrice}, hasIgData: ${!!window.igData}, hasPriceObject: ${!!window
      .igData?.price}, has${methodName}Method: ${!!window.igData?.price?.[
      methodName
    ]}`,
  );

  const price = parseIntelligemsPrice(
    rawPrice,
    `${functionName}: variantId ${variantId}`,
  );
  const finalPrice = convertIntelligemsPriceToMinorUnits(price);

  logInfo(
    `Intelligems Variant Final Price: variantId=${variantId}, context=${
      context || 'N/A'
    }, rawPrice=${rawPrice}, parsedPrice=${price}, finalPrice=${finalPrice}, finalPriceInDollars=${
      finalPrice ? finalPrice / 100 : 'undefined'
    }`,
  );

  logInfo(
    `${functionName} final result: variantId=${variantId}, parsedPrice=${price}, finalPrice=${finalPrice}`,
  );

  // ensure we either return a valid number or undefined
  return Number.isFinite(finalPrice) ? finalPrice : undefined;
}

export function getIntelligemsVariantPrice(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
  context?: string,
): number | undefined {
  return getIntelligemsPriceByVariantId(
    isIntelligemsEnabled,
    variantId,
    'price',
    context,
  );
}

export function getIntelligemsComparePriceByVariantId(
  isIntelligemsEnabled: boolean,
  variantId?: number | string,
  context?: string,
): number | null | undefined {
  const functionName = 'getIntelligemsComparePriceByVariantId';
  const methodName = 'getComparePriceByVariantId';

  logInfo(
    `${functionName} called: isIntelligemsEnabled=${isIntelligemsEnabled}, variantId=${variantId}, context=${
      context || 'N/A'
    }`,
  );

  if (!isIntelligemsEnabled || !variantId) {
    logInfo(`${functionName}: early return - not enabled or no variant ID`);
    return undefined;
  }

  // Verify intelligems is actually available at the point of use
  if (!ensureIntelligemsAvailability()) {
    logInfo(
      `${functionName}: intelligems not actually available after verification`,
    );
    return undefined;
  }

  const rawPrice = window.igData?.price?.[methodName]?.(variantId);

  logInfo(
    `Intelligems Variant Debug: variantId=${variantId}, variantIdType=${typeof variantId}, isNumeric=${!isNaN(
      Number(variantId),
    )}, context=${
      context || 'N/A'
    }, comparePrice=${rawPrice}, rawPriceType=${typeof rawPrice}, hasIgData=${!!window.igData}, hasPriceObject=${!!window
      .igData?.price}, has${methodName}Method=${!!window.igData?.price?.[
      methodName
    ]}`,
  );

  logInfo(
    `${methodName} result: variantId=${variantId}, comparePrice=${rawPrice}, rawPriceType=${typeof rawPrice}`,
  );

  logInfo(
    `${methodName}: raw result - variantId: ${variantId}, comparePrice: ${rawPrice}, rawPriceType: ${typeof rawPrice}, hasIgData: ${!!window.igData}, hasPriceObject: ${!!window
      .igData?.price}, has${methodName}Method: ${!!window.igData?.price?.[
      methodName
    ]}`,
  );

  // Special handling for compare price: distinguish between null (no compare price) and undefined (method not available)
  if (rawPrice === null || rawPrice === '') {
    logInfo(
      `${functionName}: Intelligems explicitly says no compare price (null or empty string)`,
    );
    return null;
  }

  if (rawPrice === undefined) {
    logInfo(`${functionName}: Intelligems method not available`);
    return undefined;
  }

  const price = parseIntelligemsPrice(
    rawPrice,
    `${functionName}: variantId ${variantId}`,
  );
  const finalPrice = convertIntelligemsPriceToMinorUnits(price);

  logInfo(
    `Intelligems Variant Final Price: variantId=${variantId}, context=${
      context || 'N/A'
    }, rawPrice=${rawPrice}, parsedPrice=${price}, finalPrice=${finalPrice}, finalPriceInDollars=${
      finalPrice ? finalPrice / 100 : 'undefined'
    }`,
  );

  logInfo(
    `${functionName} final result: variantId=${variantId}, parsedPrice=${price}, finalPrice=${finalPrice}`,
  );

  // ensure we either return a valid number, null, or undefined
  return Number.isFinite(finalPrice) ? finalPrice : undefined;
}

/**
 * Check if the popup can be loaded/shown
 *
 * This function consolidates the common logic used across different apps
 * to determine if the popup should be displayed. The popup is blocked when:
 * - Intelligems is enabled but not ready (waiting for it to load)
 *
 * @param appsInfo - The apps info object containing intelligems enabled status
 * @param isIntelligemsReady - Whether intelligems is ready (from ensureIntelligemsAvailability)
 * @returns true if the popup can be shown, false if it should be blocked
 */
export const canShowPopup = (
  appsInfo: { is_intelligems_enabled: boolean },
  isIntelligemsReady: boolean,
): boolean => {
  return (
    !appsInfo.is_intelligems_enabled ||
    (appsInfo.is_intelligems_enabled && isIntelligemsReady)
  );
};
